import type { SVGProps } from "react";

import { cn } from "../lib/cn";

/**
 * Cock and hen, drawn rather than typed.
 *
 * The obvious version of these is the Unicode glyphs, and that is what the app used
 * first. They are the wrong tool in this design for two reasons that only show up once
 * they are on screen: they take the *text* size of whatever line they land in, so beside
 * a 15px label they arrive at roughly nine pixels of actual mark, and their vertical
 * placement is decided by the font's own metrics — Figtree centres them near the x-height
 * rather than on the cap line, so they sit visibly low against the word next to them.
 * Neither is adjustable without moving the text too.
 *
 * As SVG they are sized by `size-*` like every other icon in the app, they sit on the
 * flex baseline the row already established, and they inherit the 2.75 stroke weight the
 * base layer puts on `.lucide` — so they read as part of the same icon set rather than as
 * two characters that wandered in from a different font.
 *
 * Geometry: a 24-box, ring of radius 5 centred so that the arm — the arrow for a cock,
 * the cross for a hen — has room without the whole mark drifting off-centre. The two are
 * deliberately the same overall height, so a column of them does not jitter.
 *
 * lucide has no Mars/Venus in the version pinned here, which is why these are local
 * rather than an import.
 */

type SexIconProps = Omit<SVGProps<SVGSVGElement>, "children">;

/** Shared with lucide's own icons so one stroke rule in the base layer covers both. */
function base(className: string | undefined): SVGProps<SVGSVGElement> {
  return {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    focusable: false,
    className: cn("lucide", className),
  };
}

/** Mars — a cock. */
export function MaleIcon({ className, ...props }: SexIconProps) {
  return (
    <svg {...base(className)} {...props}>
      <circle cx="10" cy="14" r="5.5" />
      <path d="M14.5 9.5 21 3" />
      <path d="M15.5 3H21v5.5" />
    </svg>
  );
}

/** Venus — a hen. */
export function FemaleIcon({ className, ...props }: SexIconProps) {
  return (
    <svg {...base(className)} {...props}>
      <circle cx="12" cy="8.5" r="5.5" />
      <path d="M12 14v7" />
      <path d="M8.5 18h7" />
    </svg>
  );
}

/**
 * Sex unknown — an open circle with no arm.
 *
 * Dashed rather than solid: a plain ring is the same shape the other two are built on,
 * and at 18px it reads as a cock or hen whose arm failed to render. A broken ring says
 * "not answered" without needing a legend.
 */
export function UnknownSexIcon({ className, ...props }: SexIconProps) {
  return (
    <svg {...base(className)} {...props}>
      <circle cx="12" cy="12" r="7.5" strokeDasharray="3.6 3.6" />
    </svg>
  );
}

/**
 * The mark for a stored sex value, so no screen has to keep its own map from the API's
 * three strings to a glyph.
 */
export function SexIcon({
  sex,
  className,
  ...props
}: SexIconProps & { sex: string | null | undefined }) {
  if (sex === "Male") {
    return <MaleIcon className={className} {...props} />;
  }

  if (sex === "Female") {
    return <FemaleIcon className={className} {...props} />;
  }

  return <UnknownSexIcon className={className} {...props} />;
}
